import { Cancel, CheckCircle, Info } from "@mui/icons-material";
import {
  Button,
  ButtonProps,
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogContentText,
  DialogContentTextProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  Slide,
  useTheme,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import React from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocalization } from "../hooks/useLocalization";
import { ErrorDisplay } from "../pages/users/AddNewUser/ErrorDisplay";

// Sub-component interfaces
export interface ConfirmationDialogRootProps
  extends Omit<DialogProps, "children"> {
  children: React.ReactNode;
  fullScreenOnMobile?: boolean;
  height?: React.CSSProperties["height"];
}

export interface ConfirmationDialogTitleProps
  extends Omit<DialogTitleProps, "children"> {
  children: React.ReactNode;
  showIcon?: boolean;
  iconColor?: string;
  iconType?: "delete" | "info" | "success";
}

export interface ConfirmationDialogContentProps extends DialogContentProps {
  children: React.ReactNode;
  error?: Error | null;
  showErrorMessage?: boolean;
}

export interface ConfirmationDialogTextProps
  extends Omit<DialogContentTextProps, "children"> {
  children: React.ReactNode;
}

export interface ConfirmationDialogActionsProps extends DialogActionsProps {
  children: React.ReactNode;
}

export interface ConfirmationDialogButtonProps extends ButtonProps {
  children: React.ReactNode;
  isLoading?: boolean;
  loadingText?: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Sub-components
export const ConfirmationDialogRoot: React.FC<ConfirmationDialogRootProps> = ({
  children,
  fullScreenOnMobile,
  height,
  ...props
}) => {
  const isMobile = useIsMobile();
  const isFullScreen = isMobile && fullScreenOnMobile;

  return (
    <Dialog
      TransitionComponent={isFullScreen ? Transition : undefined}
      fullScreen={props.fullScreen ?? isFullScreen}
      {...props}
      sx={{
        "& .MuiDialog-paper": {
          padding: "8px",
          borderRadius: isFullScreen ? 0 : "24px",
          maxWidth: isFullScreen ? "100%" : "488px",
          overflow: "hidden",
          height: isFullScreen ? undefined : height,
        },
        ...props.sx,
      }}
    >
      {children}
    </Dialog>
  );
};

export const ConfirmationDialogTitle: React.FC<
  ConfirmationDialogTitleProps
> = ({ children, showIcon = true, iconColor, iconType = "info", ...props }) => {
  const theme = useTheme();

  const renderIcon = () => {
    let defaultColor = theme.colors.orange;
    let IconComponent = Info;

    if (iconType === "success") {
      defaultColor = theme.colors.green;
      IconComponent = CheckCircle;
    } else if (iconType === "delete") {
      defaultColor = theme.colors.red;
      IconComponent = Cancel;
    }

    return (
      <IconComponent
        sx={{
          fontSize: "24px",
          color: iconColor ?? defaultColor,
          mr: "18px",
        }}
      />
    );
  };

  return (
    <DialogTitle
      {...props}
      sx={{
        fontSize: "24px",
        fontWeight: 700,
        color: theme.colors.text[5],
        alignItems: "center",
        display: "flex",
        ...props.sx,
      }}
    >
      {showIcon && renderIcon()}
      {children}
    </DialogTitle>
  );
};

export const ConfirmationDialogContent: React.FC<
  ConfirmationDialogContentProps
> = ({ children, error, showErrorMessage, ...props }) => {
  return (
    <DialogContent
      {...props}
      sx={{
        overflowX: "hidden",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        paddingBottom: "8px",
        ...props.sx,
      }}
    >
      {children}
      {error && (
        <ErrorDisplay error={error} showErrorMessage={showErrorMessage} />
      )}
    </DialogContent>
  );
};

export const ConfirmationDialogText: React.FC<ConfirmationDialogTextProps> = ({
  children,
  ...props
}) => {
  const theme = useTheme();

  return (
    <DialogContentText
      {...props}
      sx={{
        whiteSpace: "pre-line",
        fontSize: "14px",
        color: theme.colors.text[1],
        fontWeight: 500,
        ...props.sx,
      }}
    >
      {children}
    </DialogContentText>
  );
};

export const ConfirmationDialogActions: React.FC<
  ConfirmationDialogActionsProps
> = ({ children, ...props }) => {
  return (
    <DialogActions
      {...props}
      sx={{
        p: "24px",
        gap: "8px",
        ...props.sx,
      }}
    >
      {children}
    </DialogActions>
  );
};

export const ConfirmationDialogButton: React.FC<
  ConfirmationDialogButtonProps
> = ({ children, isLoading = false, loadingText, disabled, ...props }) => {
  const { t } = useLocalization();

  return (
    <Button
      {...props}
      disabled={isLoading || disabled}
      sx={{
        width: "100%",
        height: "40px",
        borderRadius: "8px",
        ...props.sx,
      }}
    >
      {isLoading
        ? (loadingText ?? t("components.confirmationDialog.loading"))
        : children}
    </Button>
  );
};

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonVariant?: "contained" | "outlined" | "secondary" | "text";
  cancelButtonVariant?: "contained" | "outlined" | "secondary" | "text";
  isLoading?: boolean;
  loadingText?: string;
  showIcon?: boolean;
  iconColor?: string;
  iconType?: "info" | "success";
  maxWidth?: "lg" | "md" | "sm" | "xl" | "xs" | false;
  fullScreenOnMobile?: boolean;
  height?: React.CSSProperties["height"];
}

export const ConfirmationDialog: React.FC<ConfirmationDialogProps> & {
  Root: typeof ConfirmationDialogRoot;
  Title: typeof ConfirmationDialogTitle;
  Content: typeof ConfirmationDialogContent;
  Text: typeof ConfirmationDialogText;
  Actions: typeof ConfirmationDialogActions;
  Button: typeof ConfirmationDialogButton;
} = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  confirmButtonVariant = "contained",
  cancelButtonVariant = "contained",
  isLoading = false,
  loadingText,
  showIcon = true,
  iconColor,
  iconType = "info",
  maxWidth = "xs",
  fullScreenOnMobile = true,
  height,
}) => {
  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <ConfirmationDialogRoot
      aria-describedby="confirmation-dialog-description"
      aria-labelledby="confirmation-dialog-title"
      fullScreenOnMobile={fullScreenOnMobile}
      height={height}
      maxWidth={maxWidth}
      onClose={onClose}
      open={open}
    >
      <ConfirmationDialogTitle
        iconColor={iconColor}
        iconType={iconType}
        id="confirmation-dialog-title"
        showIcon={showIcon}
      >
        {title}
      </ConfirmationDialogTitle>
      <ConfirmationDialogContent>
        <ConfirmationDialogText id="confirmation-dialog-description">
          {description}
        </ConfirmationDialogText>
      </ConfirmationDialogContent>
      <ConfirmationDialogActions>
        <ConfirmationDialogButton
          onClick={onClose}
          variant={cancelButtonVariant}
        >
          {cancelButtonText}
        </ConfirmationDialogButton>
        <ConfirmationDialogButton
          isLoading={isLoading}
          loadingText={loadingText}
          onClick={handleConfirm}
          variant={confirmButtonVariant}
        >
          {confirmButtonText}
        </ConfirmationDialogButton>
      </ConfirmationDialogActions>
    </ConfirmationDialogRoot>
  );
};

ConfirmationDialog.Root = ConfirmationDialogRoot;
ConfirmationDialog.Title = ConfirmationDialogTitle;
ConfirmationDialog.Content = ConfirmationDialogContent;
ConfirmationDialog.Text = ConfirmationDialogText;
ConfirmationDialog.Actions = ConfirmationDialogActions;
ConfirmationDialog.Button = ConfirmationDialogButton;
